import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, CircleAlert, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";

const PURPLE = "#5B2383";
const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("es-ES", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || "—"; }
};

const Stat = ({ label, value, tone = "slate" }) => (
  <div className="text-center">
    <div className={`text-xl font-black text-${tone}-700`}>{value}</div>
    <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
  </div>
);

const blankCfg = { temporada: "", competicion: "", grupo: "", tipo_juego: "", nombre_oficial: "", cod_equipo: "", url: "" };

function ConfigDialog({ team, open, onOpenChange, onSaved }) {
  const [cfg, setCfg] = useState(blankCfg);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setCfg({ ...blankCfg, ...(team?.rffm_config || {}), nombre_oficial: (team?.rffm_config?.nombre_oficial) || "" });
  }, [open, team]);

  const parseUrl = (url) => {
    try {
      const u = new URL(url);
      const q = u.searchParams;
      setCfg((c) => {
        const temporada = q.get("temporada") || c.temporada;
        const competicion = q.get("competicion") || c.competicion;
        const grupo = q.get("grupo") || c.grupo;
        const cambio = temporada !== c.temporada || competicion !== c.competicion || grupo !== c.grupo;
        return { ...c, url, temporada, competicion, grupo, cod_equipo: cambio ? "" : c.cod_equipo };
      });
      if (q.get("competicion")) toast.success("Temporada, competición y grupo extraídos de la URL");
    } catch { setCfg((c) => ({ ...c, url })); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rffm-sync/teams/${team.id}/config`, cfg);
      toast.success(`Configuración RFFM guardada para ${team.name}`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="rffm-config-dialog">
        <DialogHeader>
          <DialogTitle>Datos RFFM — {team?.name}</DialogTitle>
          <DialogDescription className="text-xs">Se configura una sola vez por temporada.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">URL de "Resultados y jornadas" (pégala y se rellenan los códigos)</Label>
            <Input value={cfg.url} onChange={(e) => parseUrl(e.target.value)} placeholder="https://www.rffm.es/competicion/resultados-y-jornadas?temporada=..." data-testid="rffm-cfg-url" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Temporada *</Label><Input value={cfg.temporada} onChange={(e) => setCfg({ ...cfg, temporada: e.target.value })} data-testid="rffm-cfg-temporada" /></div>
            <div><Label className="text-xs">Competición *</Label><Input value={cfg.competicion} onChange={(e) => setCfg({ ...cfg, competicion: e.target.value })} data-testid="rffm-cfg-competicion" /></div>
            <div><Label className="text-xs">Grupo *</Label><Input value={cfg.grupo} onChange={(e) => setCfg({ ...cfg, grupo: e.target.value })} data-testid="rffm-cfg-grupo" /></div>
          </div>
          <div>
            <Label className="text-xs">Nombre oficial del equipo en RFFM</Label>
            <Input value={cfg.nombre_oficial} onChange={(e) => setCfg({ ...cfg, nombre_oficial: e.target.value })} placeholder="EF CONCEPCION ..." data-testid="rffm-cfg-nombre" />
            <p className="text-[10px] text-slate-400 mt-1">Si se deja vacío se usa el nombre del equipo en la app. El código RFFM se detecta automáticamente en la primera sincronización.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Tipo de juego</Label><Input value={cfg.tipo_juego} onChange={(e) => setCfg({ ...cfg, tipo_juego: e.target.value })} placeholder="1" /></div>
            <div><Label className="text-xs">Cod. equipo RFFM</Label><Input value={cfg.cod_equipo} onChange={(e) => setCfg({ ...cfg, cod_equipo: e.target.value })} placeholder="(auto)" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="text-white font-bold w-full" style={{ backgroundColor: PURPLE }} data-testid="rffm-cfg-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar configuración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RffmSync() {
  const [data, setData] = useState(null);
  const [cfgTeam, setCfgTeam] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/rffm-sync/overview");
      setData(d);
      if (d.running) {
        setSyncing(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(load, 4000);
      } else {
        setSyncing((prev) => {
          if (prev) toast.success("Sincronización RFFM completada");
          return false;
        });
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line

  const run = async (teamId) => {
    try {
      const { data: r } = await api.post("/rffm-sync/run", teamId ? { team_id: teamId } : {});
      if (r.already_running) toast.info("Ya hay una sincronización en curso");
      else toast.success("Sincronización iniciada");
      setSyncing(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(load, 2500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>;
  const s = data.last_run?.summary || {};

  return (
    <div className="space-y-5" data-testid="rffm-sync-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Sincronización RFFM</h1>
          <p className="text-xs text-slate-500">Calendario oficial de la Federación · automática todos los lunes a las 07:00</p>
        </div>
        <Button onClick={() => run()} disabled={syncing} className="text-white font-bold" style={{ backgroundColor: PURPLE }} data-testid="rffm-sync-all-btn">
          {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {syncing ? "Sincronizando..." : "Sincronizar ahora"}
        </Button>
      </div>

      {/* Resumen */}
      <Card className="border-purple-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap justify-between gap-3 text-xs text-slate-600 mb-3">
            <span data-testid="rffm-last-run">Última sincronización: <b>{data.last_run ? fmt(data.last_run.finished_at) : "nunca"}</b>{data.last_run && <span className="text-slate-400"> ({data.last_run.triggered_by === "auto" ? "automática" : "manual"})</span>}</span>
            <span>Próxima automática: <b className="capitalize">{fmt(data.next_auto_run)}</b></span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            <Stat label="Equipos revisados" value={s.teams_checked ?? "—"} />
            <Stat label="Partidos revisados" value={s.matches_checked ?? "—"} />
            <Stat label="Sin cambios" value={s.unchanged ?? "—"} tone="emerald" />
            <Stat label="Actualizados" value={s.updated ?? "—"} tone="purple" />
            <Stat label="No encontrados" value={s.not_found ?? "—"} tone="amber" />
            <Stat label="Errores" value={s.errors ?? "—"} tone="rose" />
          </div>
        </CardContent>
      </Card>

      {/* Notificaciones de cambios */}
      {(data.notifications || []).length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="text-xs font-black uppercase tracking-widest text-amber-800">Cambios detectados</div>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={async () => { await api.post("/rffm-sync/notifications/read"); load(); }} data-testid="rffm-notifs-read">Marcar leídos</Button>
            </div>
            {data.notifications.map((n) => (
              <div key={n.id} className="text-xs text-amber-900"><b>{n.title}</b> — {n.body}</div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Equipos */}
      <Card className="border-slate-200">
        <CardContent className="p-0 divide-y divide-slate-100">
          <div className="p-3 text-xs font-black uppercase tracking-widest text-slate-500">
            Equipos · {data.configured_count} configurados / {data.teams.length - data.configured_count} sin configurar
          </div>
          {data.teams.map((t) => {
            const cfg = t.rffm_config || {};
            const ok = Boolean(cfg.competicion);
            return (
              <div key={t.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`rffm-team-${t.id}`}>
                {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <CircleAlert className="h-4 w-4 text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-800">{t.name} <span className="text-[10px] text-slate-400 font-semibold">{t.category}</span></div>
                  <div className="text-[10px] text-slate-400">
                    {ok ? `Temporada ${cfg.temporada} · Competición ${cfg.competicion} · Grupo ${cfg.grupo}${cfg.cod_equipo ? ` · Cod ${cfg.cod_equipo}` : ""}` : "Sin configuración RFFM"}
                  </div>
                </div>
                {ok && (
                  <Button size="sm" variant="outline" disabled={syncing} onClick={() => run(t.id)} className="h-8 text-xs" data-testid={`rffm-sync-team-${t.id}`}>
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sincronizar
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setCfgTeam(t)} className="h-8 text-xs border-purple-300 text-purple-700" data-testid={`rffm-config-team-${t.id}`}>
                  <Settings2 className="h-3.5 w-3.5 mr-1" /> {ok ? "Editar" : "Configurar"}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Registro de cambios */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">Registro de sincronización</div>
          {(data.recent_logs || []).length === 0 && <div className="text-sm text-slate-400 italic">Sin registros todavía.</div>}
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {(data.recent_logs || []).map((l) => (
              <div key={l.id} className="text-xs flex flex-wrap gap-x-2 items-baseline" data-testid="rffm-log-row">
                <span className="text-slate-400 shrink-0">{fmt(l.created_at).slice(0, 17)}</span>
                <b className="text-slate-700">{l.team_name}</b>
                <span className={`font-bold ${l.estado === "ERROR" ? "text-rose-600" : l.estado === "NO_ENCONTRADO" ? "text-amber-600" : "text-emerald-600"}`}>{l.estado}</span>
                <span className="text-slate-600">{l.campo}{l.valor_anterior || l.valor_nuevo ? `: ${l.valor_anterior || "—"} → ${l.valor_nuevo || "—"}` : ""}</span>
                {l.detalle && <span className="text-slate-400">{l.detalle}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <ConfigDialog team={cfgTeam} open={Boolean(cfgTeam)} onOpenChange={(o) => !o && setCfgTeam(null)} onSaved={load} />
    </div>
  );
}
